/**
 * DomainDkimTab — 收件域右侧"DKIM 签名"Tab
 * 展示选中收件域下的 DKIM 密钥：生成/删除/多选/分页/详情（生成自动带入选中域名）。
 */
import { useMemo, useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  makeStyles,
  Text,
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogActions,
  Field,
  InfoLabel,
  Input,
  Combobox,
  Option,
  Switch,
} from '@fluentui/react-components'
import { AddRegular } from '@fluentui/react-icons'
import { dkimApi } from '@/api/dkim'
import type { DkimKey, Domain } from '@/api/types'
import { toast } from '@/lib/toast'
import { ApiError } from '@/api/client'
import { PagedGrid, type PagedColumn } from '@/components/PagedGrid'
import { DialogCloseTitle } from '@/components/DialogCloseTitle'
import { DetailGrid, DetailField } from '@/components/DetailField'
import { formatDate } from '@/lib/format'

const PAGE_SIZE = 10

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0 },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px 16px' },
  formGridItem: { minWidth: 0 },
  dialogSurface: { width: '480px', maxWidth: '90vw' },
})

export default function DomainDkimTab({ domain }: { domain: Domain }) {
  const styles = useStyles()
  const queryClient = useQueryClient()

  const { data: keys, isLoading } = useQuery({
    queryKey: ['dkim'],
    queryFn: () => dkimApi.list(),
  })

  const domainKeys = useMemo(
    () => (keys ?? []).filter((k) => k.domain === domain.name),
    [keys, domain.name],
  )

  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)

  const filteredKeys = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return domainKeys
    return domainKeys.filter((k) => k.selector.toLowerCase().includes(q))
  }, [domainKeys, query])

  const totalPages = Math.max(1, Math.ceil(filteredKeys.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageItems = filteredKeys.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  useEffect(() => {
    setQuery('')
    setPage(1)
  }, [domain.id])

  // 生成
  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState({ domain: domain.name, selector: 'mailman', key_size: '2048' })
  const openCreate = () => {
    setForm({ domain: domain.name, selector: 'mailman', key_size: '2048' })
    setCreateOpen(true)
  }

  const generateMutation = useMutation({
    mutationFn: () =>
      dkimApi.generate({
        domain: form.domain.trim(),
        selector: form.selector.trim() || 'mailman',
        key_size: Number(form.key_size),
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['dkim'] })
      setCreateOpen(false)
      toast.success('生成成功', `已为 ${res.domain} 生成 DKIM 密钥，请在详情页查看 DNS TXT 记录`)
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : '生成失败'
      toast.error('生成失败', msg)
    },
  })

  // 多选 + 批量删除
  const [selectionMode, setSelectionMode] = useState<'single' | 'multiselect'>('single')
  const [selectedRows, setSelectedRows] = useState<DkimKey[]>([])

  const deleteMutation = useMutation({
    mutationFn: async (rows: DkimKey[]) => {
      await Promise.all(rows.map((k) => dkimApi.delete(k.id)))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dkim'] })
      toast.success('删除成功', `已删除 ${selectedRows.length} 项`)
    },
    onError: (err) => toast.error('删除失败', err instanceof ApiError ? err.message : '请稍后重试'),
  })

  // 详情
  const [detailKey, setDetailKey] = useState<DkimKey | null>(null)

  const columns: PagedColumn<DkimKey>[] = [
    {
      key: 'selector',
      header: 'Selector',
      headerTooltip: 'DNS 中定位公钥记录的主机名前缀，最终记录名为 <selector>._domainkey.<域名>',
      width: '160px',
      render: (k) => <Text weight="semibold">{k.selector}</Text>,
    },
    {
      key: 'key_size',
      header: '密钥长度',
      width: '120px',
      render: (k) => (k.key_size ? `${k.key_size} bit` : '-'),
    },
    {
      key: 'active',
      header: '状态',
      headerTooltip: '停用后该域的 DKIM 签名不再启用',
      width: '100px',
      render: (k) => (k.active ? '启用' : '停用'),
    },
    {
      key: 'created_at',
      header: '创建时间',
      width: '160px',
      render: (k) => formatDate(k.created_at),
    },
  ]

  return (
    <div className={styles.root}>
      <PagedGrid<DkimKey>
        rows={pageItems}
        columns={columns}
        loading={isLoading}
        rowKey={(k) => String(k.id)}
        total={filteredKeys.length}
        page={safePage}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        emptyText={query ? '没有匹配的密钥' : '该域名下暂无 DKIM 密钥，点击左上角「生成密钥」'}
        toolbarLeft={
          <>
            <Button appearance="primary" size="small" icon={<AddRegular />} onClick={openCreate}>
              生成密钥
            </Button>
            <Switch
              checked={selectionMode === 'multiselect'}
              onChange={(_, d) => setSelectionMode(d.checked ? 'multiselect' : 'single')}
              label="多选"
            />
          </>
        }
        selectionMode={selectionMode}
        onSelectionChange={setSelectedRows}
        onDelete={(rows) => deleteMutation.mutateAsync(rows)}
        onRowClick={(k) => setDetailKey(k)}
      />

      {/* 生成密钥 */}
      <Dialog open={createOpen} onOpenChange={(_, d) => { if (!d.open) setCreateOpen(false) }} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="生成 DKIM 密钥" onClose={() => setCreateOpen(false)} />
            <DialogContent>
              <div className={styles.formGrid}>
                <Field className={styles.formGridItem} label={<InfoLabel info="要为哪个邮件域生成 DKIM 密钥。默认取左侧选中的收件域，也可手动修改。">域名</InfoLabel>} required>
                  <Input value={form.domain} onChange={(_, d) => setForm((f) => ({ ...f, domain: d.value }))} placeholder="example.com" />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info={'Selector 是 DNS 中定位公钥记录的主机名前缀，最终记录名为 「<selector>._domainkey.<域名>」。可自行起名并随时更换，但需与域名服务商处发布的 TXT 记录保持一致；建议用固定简短值（默认 mailman），换域或轮换密钥时可改用不同 Selector。'}>Selector</InfoLabel>}>
                  <Input value={form.selector} onChange={(_, d) => setForm((f) => ({ ...f, selector: d.value }))} placeholder="mailman" />
                </Field>
                <Field className={styles.formGridItem} label={<InfoLabel info="密钥的 RSA 长度，越长越安全但签名更慢。">密钥长度</InfoLabel>}>
                  <Combobox value={form.key_size} selectedOptions={[form.key_size]} onOptionSelect={(_, d) => setForm((f) => ({ ...f, key_size: d.selectedOptions[0] }))}>
                    <Option value="2048">2048 bit（推荐）</Option>
                    <Option value="1024">1024 bit</Option>
                    <Option value="4096">4096 bit</Option>
                  </Combobox>
                </Field>
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" disabled={generateMutation.isPending || !form.domain.trim()} onClick={() => generateMutation.mutate()}>
                {generateMutation.isPending ? '生成中…' : '生成'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* 详情 */}
      <Dialog open={detailKey !== null} onOpenChange={(_, d) => !d.open && setDetailKey(null)} modalType="alert">
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogCloseTitle title="DKIM 密钥详情" onClose={() => setDetailKey(null)} />
            <DialogContent>
              {detailKey && (
                <DetailGrid key={detailKey.id}>
                  <DetailField label="ID" value={detailKey.id} mono />
                  <DetailField label="域名" value={detailKey.domain} mono help="该域名出站邮件使用此密钥进行 DKIM 签名。" />
                  <DetailField label="Selector" value={detailKey.selector} mono help="DKIM 选择器，用于在 DNS 中定位公钥记录。" />
                  <DetailField label="密钥长度" value={detailKey.key_size ? `${detailKey.key_size} bit` : '-'} />
                  <DetailField label="状态" value={detailKey.active ? '启用' : '停用'} help="停用后该域的 DKIM 签名不再启用。" />
                  <DetailField label="DNS 记录名" value={detailKey.dns_name ?? '-'} mono help="需发布的 DNS TXT 记录主机名。" />
                  <DetailField label="DNS 记录" value={detailKey.dns_record ?? '-'} mono colSpan help="需发布的 DNS TXT 记录内容，用于验证公钥签名。" />
                  <DetailField label="创建时间" value={detailKey.created_at} />
                </DetailGrid>
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}